import React, { useState } from 'react';
import { Sparkles, Wand2, Download, Save, Calendar, X, Loader2, Image as ImageIcon, MessageSquarePlus, AlertCircle } from 'lucide-react';
import { useApp } from '../contexts/AppContext';
import { useAuth } from '../contexts/AuthContext';
import { generateImage, buildPrompt, suggestPrompts } from '../services/aiService';
import { uploadLibraryImage } from '../services/libraryService';
import ImageRefinementPanel from '../components/ImageRefinementPanel';
import MediaLibrarySelector from '../components/MediaLibrarySelector';
import PromptBuilderChat from '../components/PromptBuilderChat';

const CreativeGeneration = () => {
    const { brands, addCreative, selectedBrand } = useApp();
    // const [selectedBrand, setSelectedBrand] = useState(''); // Removed local state
    const [format, setFormat] = useState('social-post');
    const [mode, setMode] = useState('full-ai'); // 'asset-based' or 'full-ai'
    const [generating, setGenerating] = useState(false);
    const [generatedVariations, setGeneratedVariations] = useState([]);

    // Prompt Wizard State
    const [referenceImages, setReferenceImages] = useState([]);
    const [showLibrarySelector, setShowLibrarySelector] = useState(false);
    const [showPromptChat, setShowPromptChat] = useState(false);

    // Image selection workflow
    const [showImageSelector, setShowImageSelector] = useState(false);
    const [crawledImages, setCrawledImages] = useState([]);
    const [selectedImages, setSelectedImages] = useState([]);
    const [loadingImages, setLoadingImages] = useState(false);
    const [editedImages, setEditedImages] = useState({});

    const [promptInputs, setPromptInputs] = useState({
        objective: 'awareness',
        visualStyle: 'modern and clean',
        platform: 'instagram',
        constraints: [],
    });

    const [finalPrompt, setFinalPrompt] = useState('');

    const formatOptions = [
        { value: 'social-post', label: 'Social Media Post', icon: '📱' },
        { value: 'display-ad', label: 'Display Ad', icon: '🖼️' },
        { value: 'video-ad', label: 'Video Ad', icon: '🎬' },
        { value: 'story', label: 'Story/Reel', icon: '📹' },
    ];

    const objectiveOptions = [
        { value: 'awareness', label: 'Brand Awareness' },
        { value: 'conversion', label: 'Conversion' },
        { value: 'engagement', label: 'Engagement' },
    ];

    const platformOptions = [
        { value: 'instagram', label: 'Instagram' },
        { value: 'facebook', label: 'Facebook' },
        { value: 'linkedin', label: 'LinkedIn' },
        { value: 'tiktok', label: 'TikTok' },
        { value: 'google', label: 'Google Ads' },
    ];

    // const brand = brands.find(b => b.id === selectedBrand); // Removed
    const brand = selectedBrand; // Use context value directly

    const handleAddFromLibrary = (items) => {
        const newImages = items.map((item, index) => ({
            id: `ref-lib-${item.id}`,
            url: item.imageUrl,
            status: 'valid',
            label: `[Image ${String.fromCharCode(65 + referenceImages.length + index)}]`
        }));
        setReferenceImages(prev => [...prev, ...newImages]);
    };

    const handleUploadRefImages = (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        files.forEach((file, index) => {
            const nextLabel = `[Image ${String.fromCharCode(65 + referenceImages.length + index)}]`;
            const newImage = { id: `ref-upload-${Date.now()}-${index}`, url: '', status: 'loading', label: nextLabel };
            setReferenceImages(prev => [...prev, newImage]);

            const reader = new FileReader();
            reader.onload = (event) => {
                setReferenceImages(prev =>
                    prev.map(i => i.id === newImage.id ? { ...i, url: event.target.result, status: 'valid' } : i)
                );
            };
            reader.readAsDataURL(file);
        });
        e.target.value = ''; // Reset input
    };

    const handleRemoveRefImage = (id) => {
        setReferenceImages(prev => prev.filter(img => img.id !== id));
    };

    const handleBuildPrompt = () => {
        const prompt = buildPrompt({
            ...promptInputs,
            brandVoice: brand?.voice?.tone,
            brand: brand, // Pass the entire brand object
        });
        setFinalPrompt(prompt);
    };

    const handleCrawlImages = async () => {
        if (!brand?.website) {
            alert('No website URL found for this brand. Please add a website URL in brand settings.');
            return;
        }

        setLoadingImages(true);
        setShowImageSelector(true);

        try {
            const response = await fetch('http://localhost:3001/api/crawl-images', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ url: brand.website }),
            });

            if (!response.ok) throw new Error('Failed to crawl images');

            const data = await response.json();
            setCrawledImages(data.images);
        } catch (error) {
            console.error('Image crawling error:', error);
            alert('Failed to crawl images: ' + error.message);
            setShowImageSelector(false);
        } finally {
            setLoadingImages(false);
        }
    };

    const handleSelectImage = (image) => {
        setSelectedImages(prev => {
            const isSelected = prev.some(img => img.url === image.url);
            if (isSelected) {
                return prev.filter(img => img.url !== image.url);
            } else {
                return [...prev, image];
            }
        });
    };

    // Refinement state
    const { currentUser } = useAuth();
    const [refiningImage, setRefiningImage] = useState(null); // The image object currently being refined
    const [isRefining, setIsRefining] = useState(false);
    const [refinementError, setRefinementError] = useState(null);

    const handleRefineClick = (image) => {
        setRefiningImage(image);
        setRefinementError(null);
    };

    const handleRefineImage = async (prompt, userReferenceImages) => {
        if (!refiningImage || !currentUser?.uid) return;

        // Filter to only valid reference images
        const validRefImages = userReferenceImages.filter(img => img.status === 'valid' && img.url);

        setIsRefining(true);
        setRefinementError(null);

        try {
            // Build reference images array for API
            // Consistently label the base image as [ORIGINAL_IMAGE] to help the AI distinguish it from style references
            const referenceImages = [{
                url: refiningImage.url,
                instruction: `[ORIGINAL_IMAGE]
This is the base image we are refining. You must maintain its core composition, subjects, and layout while applying the requested changes. All other images provided are for style or detail reference only.`
            }];

            // Add user's labeled reference images
            validRefImages.forEach((refImg) => {
                referenceImages.push({
                    url: refImg.url,
                    instruction: `[REFERENCE: ${refImg.label}]
Use this image as a reference for the ${refImg.label} requested. Apply its style, colors, or specific elements only as directed in the refinement prompt.`
                });
            });

            // Build reference image legend for the prompt
            const refImageLegend = validRefImages.length > 0
                ? `\n\nREFERENCE IMAGES PROVIDED:\n${validRefImages.map(img => `- ${img.label}: User reference image`).join('\n')}\nWhen the user mentions these labels, apply elements from that specific reference image.`
                : '';

            // Detect if this is a removal/editing request vs a style change
            const lowerPrompt = prompt.toLowerCase();
            const isRemovalRequest = lowerPrompt.includes('remove') || lowerPrompt.includes('delete') || lowerPrompt.includes('erase') || lowerPrompt.includes('get rid of');
            const isReplaceRequest = lowerPrompt.includes('replace') || lowerPrompt.includes('swap') || lowerPrompt.includes('change to');

            // Special case: Background removal requests need true transparency
            // Gemini can't output transparent PNGs, so we use a two-step process:
            // 1. Ask Gemini to put the subject on a solid green background
            // 2. Use the background removal API to extract true alpha transparency
            const isBackgroundRemovalRequest = lowerPrompt.includes('background') && (
                lowerPrompt.includes('remove') ||
                lowerPrompt.includes('transparent') ||
                lowerPrompt.includes('delete') ||
                lowerPrompt.includes('cut out') ||
                lowerPrompt.includes('cutout') ||
                lowerPrompt.includes('extract')
            );

            // If it's a background removal request, use direct AI background removal
            if (isBackgroundRemovalRequest) {
                console.log('🎨 Background removal request detected - using AI background removal');
                setRefinementError('Removing background with AI...');

                // Call the background removal API directly on the original image
                const bgRemovalResponse = await fetch('http://localhost:3001/api/remove-background', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        imageUrl: refiningImage.url,
                        userId: currentUser.uid
                    })
                });

                if (!bgRemovalResponse.ok) {
                    const errorData = await bgRemovalResponse.json().catch(() => ({}));
                    throw new Error(errorData.details || errorData.error || 'Background removal failed');
                }

                const bgResult = await bgRemovalResponse.json();

                // Update the edited images map with the transparent result
                setEditedImages(prev => ({
                    ...prev,
                    [refiningImage.url]: {
                        editedImageUrl: bgResult.url,
                        prompt: prompt.trim()
                    }
                }));

                setRefiningImage(null);
                setRefinementError(null);
                alert('Background removed successfully! The image now has a transparent background.');
                return;
            }

            // Build a structured refinement prompt that clearly identifies the original vs reference images
            let refinementPrompt;

            if (isRemovalRequest || isReplaceRequest) {
                refinementPrompt = `IMAGE EDITING REQUEST - MODIFY THE PROVIDED IMAGE:

ORIGINAL IMAGE: The image labeled [ORIGINAL_IMAGE] shows "${refiningImage.alt || 'a scene'}".
${refImageLegend}

EDITING INSTRUCTION: ${prompt.trim()}

YOU MUST:
1. Use [ORIGINAL_IMAGE] as the EXACT base to edit.
2. Keep ALL elements that are NOT mentioned in the editing instruction EXACTLY as they appear.
3. ${isRemovalRequest ? 'REMOVE the specified element(s) and fill the area naturally.' : 'REPLACE the specified element(s) as instructed.'}
4. Do NOT change the overall composition, lighting, or style.
5. The result should look like a natural edit of the original, not a new image.

This is an EDIT operation, not a regeneration. The output must be recognizably the same image with only requested changes.`;
            } else {
                // For style/enhancement requests - CONDITIONAL STYLE TRANSFER
                refinementPrompt = `IMAGE REFINEMENT REQUEST:

Target: The image labeled [ORIGINAL_IMAGE].
${refImageLegend}

USER'S REFINEMENT GOAL: ${prompt.trim()}

INSTRUCTIONS:
1. **Contextual Awareness**: You have been provided with an [ORIGINAL_IMAGE] and possibly several REFERENCE images.
2. **OPTIONAL STYLE TRANSFER**: Only apply fonts, colors, or design styles from the reference images IF the user explicitly asks for them (e.g., "Use the font from [REFERENCE: ...]").
3. **Default Behavior**: Focus primarily on modifying [ORIGINAL_IMAGE] based on the USER'S REFINEMENT GOAL.
4. **Merge Logic**: If requested, merge style characteristics from references into the composition of the original.

CRITICAL: [ORIGINAL_IMAGE] is the base. Maintain its core subjects and composition.`;
            }

            // Call the image generation API with retry logic for overloaded model
            let response;
            let retries = 0;
            const MAX_RETRIES = 1;

            while (retries <= MAX_RETRIES) {
                response = await fetch('http://localhost:3001/api/generate-image-gemini', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        prompt: refinementPrompt,
                        aspectRatio: '1:1',
                        referenceImages: referenceImages
                    })
                });

                if (response.ok) break;

                const errorText = await response.text();
                let errorData = {};
                try { errorData = JSON.parse(errorText); } catch (e) { }

                const isOverloaded = response.status === 500 && (
                    errorData.details?.toLowerCase().includes('overloaded') ||
                    errorData.error?.toLowerCase().includes('overloaded')
                );

                if (isOverloaded && retries < MAX_RETRIES) {
                    retries++;
                    console.log(`⚠️ Model overloaded. Retrying in 60 seconds (Attempt ${retries}/${MAX_RETRIES})...`);
                    setRefinementError(`Model is overloaded. Automatic retry in 60 seconds...`);
                    await new Promise(resolve => setTimeout(resolve, 60000));
                    setRefinementError(null);
                    continue;
                }

                console.error('API error:', response.status, errorText);
                throw new Error(errorData.details || errorData.error || 'Failed to generate refined image');
            }

            const data = await response.json();
            if (!data.url) {
                throw new Error('No image URL in response');
            }

            let finalImageUrl = data.url;
            if (finalImageUrl.startsWith('data:image')) {
                // Upload to storage if it's base64, so we have a permanent URL
                // We use 'generated-assets' as the type or folder
                finalImageUrl = await uploadLibraryImage(
                    finalImageUrl,
                    currentUser.uid,
                    selectedBrand.id,
                    'generated-assets'
                );
            }

            // Update the edited images map
            setEditedImages(prev => ({
                ...prev,
                [refiningImage.url]: {
                    editedImageUrl: finalImageUrl,
                    prompt: prompt.trim()
                }
            }));

            // Close modal after success
            setRefiningImage(null);
            alert('Image refined successfully! The refined version will be used for generation.');

        } catch (error) {
            console.error('Refinement failed:', error);
            setRefinementError(error.message || 'Failed to refine image');
        } finally {
            setIsRefining(false);
        }
    };

    const handleGenerate = async () => {
        if (!finalPrompt || !selectedBrand) return;

        setGenerating(true);
        try {
            // Generate 3 variations with brand context
            const options = {
                brand: brand,
                format: format,
                platform: promptInputs.platform,
                selectedImages: selectedImages, // Include selected images
                editedImages: editedImages, // Include edited/transformed images
                referenceImages: referenceImages.map(img => ({
                    url: img.url,
                    instruction: `[REFERENCE: ${img.label}]\nUse this image as a reference for style/content/structure.`
                })),
            };

            const results = await Promise.all([
                generateImage(finalPrompt + ' - Variation 1', options),
                generateImage(finalPrompt + ' - Variation 2 with different visual approach', options),
                generateImage(finalPrompt + ' - Variation 3 with alternative copy angle', options),
            ]);

            console.log('Generated results:', results);
            setGeneratedVariations(results);
        } catch (error) {
            console.error('Generation error:', error);
            alert('Failed to generate creatives: ' + error.message + '\n\nMake sure the server is running (npm run server)');
        }
        setGenerating(false);
    };

    const handleSaveCreative = (variation, index) => {
        const creative = {
            brandId: selectedBrand.id, // Use ID from object
            brandName: brand.name,
            title: `${brand.name} - ${format} ${index + 1}`,
            format,
            platforms: [promptInputs.platform],
            imageUrl: variation.url,
            copy: variation.copy,
            visual: variation.visual,
            design: variation.design,
            htmlTemplate: variation.htmlTemplate,
            prompt: finalPrompt,
        };

        addCreative(creative);
        alert('Creative saved to library!');
    };

    const handleDownloadCreative = (variation, index, downloadFormat = 'html') => {
        const filename = `${brand.name.replace(/\s+/g, '_')}_${format}_${index + 1}`;

        if (downloadFormat === 'html') {
            // Download as editable HTML file
            const blob = new Blob([variation.htmlTemplate], { type: 'text/html' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${filename}.html`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } else if (downloadFormat === 'json') {
            // Download creative data as JSON for further processing
            const creativeData = {
                brand: brand.name,
                format: format,
                platform: promptInputs.platform,
                visual: variation.visual,
                copy: variation.copy,
                design: variation.design,
                htmlTemplate: variation.htmlTemplate,
                prompt: finalPrompt,
                generatedAt: variation.generatedAt,
            };

            const blob = new Blob([JSON.stringify(creativeData, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${filename}.json`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } else if (downloadFormat === 'txt') {
            // Download copy as text file
            const copyText = `${variation.copy.headline}\n\n${variation.copy.body}\n\n${variation.copy.cta}\n\n---\nVisual Description:\n${variation.visual.description}`;
            const blob = new Blob([copyText], { type: 'text/plain' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${filename}_copy.txt`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        }
    };

    return (
        <div className="animate-fadeIn">
            <div style={{ marginBottom: 'var(--space-8)' }}>
                <h1>AI Creative Generation</h1>
                <p className="text-secondary">Generate stunning ad creatives powered by AI in seconds.</p>
            </div>

            <div className="grid grid-cols-3 gap-6">
                {/* Left Panel - Configuration */}
                <div className="card" style={{ gridColumn: 'span 1' }}>
                    <h3 style={{ marginBottom: 'var(--space-6)' }}>Configuration</h3>

                    {/* Removed Brand Selector */}
                    {brand ? (
                        <div style={{ padding: 'var(--space-3)', background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-md)', marginBottom: 'var(--space-4)', borderLeft: '4px solid var(--color-primary)' }}>
                            <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, marginBottom: 'var(--space-1)' }}>
                                Active Brand
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                <span style={{ fontSize: '1.2em' }}>{brand.logo}</span>
                                <span style={{ fontWeight: 500 }}>{brand.name}</span>
                            </div>
                        </div>
                    ) : (
                        <div style={{ padding: 'var(--space-3)', background: 'var(--color-warning-bg)', borderRadius: 'var(--radius-md)', marginBottom: 'var(--space-4)', color: 'var(--color-warning-text)' }}>
                            ⚠️ Please select a brand from the sidebar to continue.
                        </div>
                    )}

                    {brand && (
                        <div style={{ padding: 'var(--space-3)', background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-md)', marginBottom: 'var(--space-4)' }}>
                            <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, marginBottom: 'var(--space-1)' }}>
                                Brand Voice
                            </div>
                            <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                                {brand.voice.tone}
                            </div>
                        </div>
                    )}

                    <div className="input-group">
                        <label className="input-label">Format</label>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-2)' }}>
                            {formatOptions.map(opt => (
                                <button
                                    key={opt.value}
                                    className={`btn ${format === opt.value ? 'btn-primary' : 'btn-secondary'}`}
                                    onClick={() => setFormat(opt.value)}
                                    style={{ fontSize: 'var(--font-size-sm)', padding: 'var(--space-2)' }}
                                >
                                    {opt.icon} {opt.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="input-group">
                        <label className="input-label">Generation Mode</label>
                        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                            <button
                                className={`btn ${mode === 'full-ai' ? 'btn-primary' : 'btn-secondary'}`}
                                onClick={() => setMode('full-ai')}
                                style={{ flex: 1 }}
                            >
                                Full AI
                            </button>
                            <button
                                className={`btn ${mode === 'asset-based' ? 'btn-primary' : 'btn-secondary'}`}
                                onClick={() => setMode('asset-based')}
                                style={{ flex: 1 }}
                            >
                                Asset-Based
                            </button>
                        </div>
                    </div>

                    <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: 'var(--space-4)', marginTop: 'var(--space-4)' }}>
                        <h4 style={{ marginBottom: 'var(--space-4)' }}>Prompt Wizard</h4>

                        <div className="input-group">
                            <label className="input-label">Objective</label>
                            <select
                                className="select-field"
                                value={promptInputs.objective}
                                onChange={(e) => setPromptInputs({ ...promptInputs, objective: e.target.value })}
                            >
                                {objectiveOptions.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>

                        <div className="input-group">
                            <label className="input-label">Key Message (optional context)</label>
                            <input
                                type="text"
                                className="input-field"
                                placeholder="e.g., Summer sale"
                                value={promptInputs.keyMessage || ''}
                                onChange={(e) => setPromptInputs({ ...promptInputs, keyMessage: e.target.value })}
                            />
                        </div>

                        <div className="input-group">
                            <label className="input-label">Visual Style (optional context)</label>
                            <input
                                type="text"
                                className="input-field"
                                value={promptInputs.visualStyle}
                                onChange={(e) => setPromptInputs({ ...promptInputs, visualStyle: e.target.value })}
                            />
                        </div>

                        <div className="input-group">
                            <label className="input-label">Platform</label>
                            <select
                                className="select-field"
                                value={promptInputs.platform}
                                onChange={(e) => setPromptInputs({ ...promptInputs, platform: e.target.value })}
                            >
                                {platformOptions.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>

                        <div className="input-group" style={{ marginTop: 'var(--space-4)', borderTop: '1px solid var(--color-border)', paddingTop: 'var(--space-4)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-2)' }}>
                                <label className="input-label" style={{ margin: 0 }}>AI Prompt</label>
                                <button
                                    className="btn btn-sm btn-ghost"
                                    onClick={() => setShowPromptChat(true)}
                                    style={{ color: 'var(--color-primary)', display: 'flex', alignItems: 'center', gap: '4px' }}
                                >
                                    <Sparkles size={14} />
                                    AI Assistant
                                </button>
                            </div>
                            <textarea
                                className="textarea-field"
                                placeholder="Describe the ad creative you want to generate..."
                                value={finalPrompt}
                                onChange={(e) => setFinalPrompt(e.target.value)}
                                rows={6}
                                style={{ width: '100%', resize: 'vertical' }}
                            />
                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'var(--space-2)' }}>
                                <button
                                    className="btn btn-sm btn-secondary"
                                    onClick={handleBuildPrompt}
                                    title="Append context fields to prompt"
                                >
                                    <Wand2 size={12} /> Auto-Complete from Context
                                </button>
                            </div>
                        </div>

                        {/* Reference Images Section */}
                        <div className="input-group">
                            <label className="input-label">Reference Images (Optional)</label>

                            {referenceImages.length > 0 && (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                                    {referenceImages.map((img) => (
                                        <div key={img.id} style={{ position: 'relative', width: '60px', height: '60px', borderRadius: 'var(--radius-md)', overflow: 'hidden', border: '1px solid var(--color-border)' }}>
                                            <img src={img.url} alt="Ref" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            <button
                                                onClick={() => handleRemoveRefImage(img.id)}
                                                style={{ position: 'absolute', top: 0, right: 0, background: 'rgba(0,0,0,0.5)', color: 'white', border: 'none', cursor: 'pointer', padding: '2px', lineHeight: 1 }}
                                            >
                                                <X size={12} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                                <button
                                    className="btn btn-secondary"
                                    style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-2)' }}
                                    onClick={() => setShowLibrarySelector(true)}
                                >
                                    <ImageIcon size={16} />
                                    Library
                                </button>
                                <label className="btn btn-secondary" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-2)', cursor: 'pointer', margin: 0 }}>
                                    <Download size={16} />
                                    Upload
                                    <input type="file" multiple accept="image/*" style={{ display: 'none' }} onChange={handleUploadRefImages} />
                                </label>
                            </div>
                        </div>


                        {brand?.website && (
                            <button
                                className="btn btn-secondary"
                                style={{ width: '100%', marginBottom: 'var(--space-3)', marginTop: 'var(--space-2)' }}
                                onClick={handleCrawlImages}
                                disabled={loadingImages}
                            >
                                <Download size={16} />
                                {loadingImages ? 'Loading Website Images...' : `Select Website Images (${selectedImages.length})`}
                            </button>
                        )}

                        {!brand?.website && selectedBrand && (
                            <div style={{
                                padding: 'var(--space-3)',
                                background: 'var(--color-warning-bg)',
                                borderRadius: 'var(--radius-md)',
                                marginBottom: 'var(--space-3)',
                                fontSize: 'var(--font-size-sm)',
                                color: 'var(--color-warning-text)',
                            }}>
                                ⚠️ Add a website URL to this brand to select images from their website
                            </div>
                        )}

                        <button
                            className="btn btn-primary"
                            style={{ width: '100%', marginTop: 'var(--space-4)' }}
                            onClick={handleGenerate}
                            disabled={!finalPrompt || !selectedBrand || generating}
                        >
                            {generating ? (
                                <>
                                    <Loader2 size={20} className="animate-spin" />
                                    Generating...
                                </>
                            ) : (
                                <>
                                    <Sparkles size={20} />
                                    Generate Creative
                                </>
                            )}
                        </button>
                    </div>
                </div>

                {/* Right Panel - Preview & Results */}
                <div style={{ gridColumn: 'span 2' }}>
                    <div className="card">
                        <h3 style={{ marginBottom: 'var(--space-6)' }}>Generated Variations</h3>

                        {generating && (
                            <div style={{ textAlign: 'center', padding: 'var(--space-16)' }}>
                                <div className="spinner" style={{ margin: '0 auto var(--space-4)' }} />
                                <p>AI is generating your creatives...</p>
                                <div className="progress" style={{ maxWidth: '300px', margin: '0 auto' }}>
                                    <div className="progress-bar animate-pulse" style={{ width: '60%' }} />
                                </div>
                            </div>
                        )}

                        {!generating && generatedVariations.length === 0 && (
                            <div className="empty-state">
                                <Sparkles size={64} className="empty-state-icon" />
                                <h3 className="empty-state-title">No creatives generated yet</h3>
                                <p className="empty-state-description">
                                    Configure your settings and click Generate to create AI-powered ad creatives
                                </p>
                            </div>
                        )}

                        {!generating && generatedVariations.length > 0 && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
                                {generatedVariations.map((variation, index) => (
                                    <div key={index} className="card">
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-6)' }}>
                                            {/* Preview */}
                                            <div>
                                                <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, marginBottom: 'var(--space-3)', color: 'var(--color-primary)' }}>
                                                    Preview - Variation {index + 1}
                                                </div>
                                                <div
                                                    style={{
                                                        width: '100%',
                                                        height: '400px',
                                                        borderRadius: 'var(--radius-md)',
                                                        overflow: 'hidden',
                                                        marginBottom: 'var(--space-3)',
                                                        border: '1px solid var(--color-border)',
                                                        background: '#f5f5f5',
                                                        position: 'relative',
                                                    }}
                                                >
                                                    {variation.htmlTemplate ? (
                                                        <div style={{ width: '100%', height: '100%', overflow: 'auto' }}>
                                                            <iframe
                                                                srcDoc={variation.htmlTemplate}
                                                                style={{
                                                                    width: '1080px',
                                                                    height: '1080px',
                                                                    border: 'none',
                                                                    display: 'block',
                                                                    transform: 'scale(0.37)',
                                                                    transformOrigin: 'top left',
                                                                }}
                                                                title={`Preview ${index + 1}`}
                                                                sandbox="allow-same-origin"
                                                                scrolling="no"
                                                            />
                                                        </div>
                                                    ) : (
                                                        <div style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            height: '100%',
                                                            color: 'var(--color-text-secondary)',
                                                        }}>
                                                            No preview available
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Action Buttons */}
                                                <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                                                    <button
                                                        className="btn btn-sm btn-primary"
                                                        onClick={() => handleDownloadCreative(variation, index, 'html')}
                                                        title="Download as editable HTML file"
                                                    >
                                                        <Download size={14} />
                                                        HTML
                                                    </button>
                                                    <button
                                                        className="btn btn-sm btn-secondary"
                                                        onClick={() => handleDownloadCreative(variation, index, 'json')}
                                                        title="Download complete creative data"
                                                    >
                                                        <Download size={14} />
                                                        JSON
                                                    </button>
                                                    <button
                                                        className="btn btn-sm btn-secondary"
                                                        onClick={() => handleDownloadCreative(variation, index, 'txt')}
                                                        title="Download ad copy as text"
                                                    >
                                                        <Download size={14} />
                                                        Copy
                                                    </button>
                                                    <button
                                                        className="btn btn-sm btn-ghost"
                                                        style={{ flex: 1 }}
                                                        onClick={() => handleSaveCreative(variation, index)}
                                                    >
                                                        <Save size={14} />
                                                        Save to Library
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Creative Details */}
                                            <div>
                                                <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, marginBottom: 'var(--space-3)' }}>
                                                    Creative Details
                                                </div>

                                                {/* Ad Copy */}
                                                <div style={{ marginBottom: 'var(--space-4)' }}>
                                                    <div style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 'var(--space-2)' }}>
                                                        HEADLINE
                                                    </div>
                                                    <div style={{ fontSize: 'var(--font-size-md)', fontWeight: 600, marginBottom: 'var(--space-3)' }}>
                                                        {variation.copy?.headline}
                                                    </div>

                                                    <div style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 'var(--space-2)' }}>
                                                        BODY
                                                    </div>
                                                    <div style={{ fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-3)', lineHeight: 1.6 }}>
                                                        {variation.copy?.body}
                                                    </div>

                                                    <div style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 'var(--space-2)' }}>
                                                        CALL-TO-ACTION
                                                    </div>
                                                    <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-primary)' }}>
                                                        {variation.copy?.cta}
                                                    </div>
                                                </div>

                                                {/* Visual Description */}
                                                <div style={{ padding: 'var(--space-3)', background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-md)', marginBottom: 'var(--space-3)' }}>
                                                    <div style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600, marginBottom: 'var(--space-2)' }}>
                                                        Visual Style
                                                    </div>
                                                    <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
                                                        {variation.visual?.description}
                                                    </div>
                                                </div>

                                                {/* Design Specs */}
                                                {variation.design && (
                                                    <div style={{ padding: 'var(--space-3)', background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-md)' }}>
                                                        <div style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600, marginBottom: 'var(--space-2)' }}>
                                                            Design Specifications
                                                        </div>
                                                        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
                                                            <div><strong>Fonts:</strong> {variation.design.fonts}</div>
                                                            <div><strong>Layout:</strong> {variation.design.layout}</div>
                                                            <div><strong>Sizing:</strong> {variation.design.sizing}</div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Refinement Modal */}
            {
                refiningImage && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0, 0, 0, 0.75)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1100,
                        padding: 'var(--space-4)',
                    }}>
                        <div className="card" style={{
                            maxWidth: '600px',
                            width: '100%',
                            maxHeight: '90vh',
                            display: 'flex',
                            flexDirection: 'column',
                            padding: 0,
                            overflow: 'hidden'
                        }}>
                            <div style={{
                                padding: 'var(--space-4)',
                                borderBottom: '1px solid var(--color-border)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                            }}>
                                <h3 style={{ margin: 0, fontSize: 'var(--font-size-lg)' }}>Refine Image</h3>
                                <button
                                    onClick={() => setRefiningImage(null)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        padding: 'var(--space-1)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <X size={24} />
                                </button>
                            </div>

                            <div style={{ padding: 'var(--space-4)', overflowY: 'auto' }}>
                                <div style={{
                                    width: '100%',
                                    aspectRatio: '16/9',
                                    background: '#f5f5f5',
                                    borderRadius: 'var(--radius-md)',
                                    overflow: 'hidden',
                                    marginBottom: 'var(--space-4)',
                                    border: '1px solid var(--color-border)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <img
                                        src={editedImages[refiningImage.url]?.editedImageUrl || refiningImage.url}
                                        alt="Refining target"
                                        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                    />
                                </div>

                                <ImageRefinementPanel
                                    onRefine={handleRefineImage}
                                    isRefining={isRefining}
                                    error={refinementError}
                                    userId={currentUser?.uid}
                                    brandId={selectedBrand?.id}
                                    originalImageUrl={refiningImage.url}
                                    initialPrompt={finalPrompt || ''}
                                />
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Image Selector Modal */}
            {
                showImageSelector && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0, 0, 0, 0.7)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000,
                        padding: 'var(--space-4)',
                    }}>
                        <div className="card" style={{
                            maxWidth: '1200px',
                            width: '100%',
                            maxHeight: '90vh',
                            overflow: 'auto',
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-6)' }}>
                                <h2>Select Images from {brand?.name}</h2>
                                <button
                                    className="btn btn-ghost"
                                    onClick={() => setShowImageSelector(false)}
                                >
                                    Close
                                </button>
                            </div>

                            {loadingImages ? (
                                <div style={{ textAlign: 'center', padding: 'var(--space-8)' }}>
                                    <div className="spinner" style={{ margin: '0 auto' }} />
                                    <p style={{ marginTop: 'var(--space-4)' }}>Crawling images from website...</p>
                                </div>
                            ) : (
                                <>
                                    <div style={{ marginBottom: 'var(--space-4)', padding: 'var(--space-3)', background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-md)' }}>
                                        <p style={{ fontSize: 'var(--font-size-sm)' }}>
                                            Found {crawledImages.length} images. Select images to use in your ad creative.
                                            Click "Refine with AI" to edit or enhance specific images.
                                        </p>
                                    </div>

                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                                        gap: 'var(--space-4)',
                                    }}>
                                        {crawledImages.map((image, index) => {
                                            const isSelected = selectedImages.some(img => img.url === image.url);
                                            const editedVersion = editedImages[image.url];

                                            return (
                                                <div
                                                    key={index}
                                                    className="card"
                                                    style={{
                                                        border: isSelected ? '2px solid var(--color-primary)' : '1px solid var(--color-border)',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.2s',
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            width: '100%',
                                                            height: '150px',
                                                            background: '#f5f5f5',
                                                            borderRadius: 'var(--radius-md)',
                                                            overflow: 'hidden',
                                                            marginBottom: 'var(--space-2)',
                                                            position: 'relative',
                                                        }}
                                                        onClick={() => handleSelectImage(image)}
                                                    >
                                                        <img
                                                            src={editedVersion?.editedImageUrl || image.url}
                                                            alt={image.alt || 'Website image'}
                                                            style={{
                                                                width: '100%',
                                                                height: '100%',
                                                                objectFit: 'cover',
                                                                filter: editedVersion?.cssFilters && !editedVersion?.editedImageUrl ? editedVersion.cssFilters : 'none',
                                                            }}
                                                            onError={(e) => {
                                                                e.target.style.display = 'none';
                                                                e.target.parentElement.innerHTML = '<div style="display:flex;align-items:center;justify-content:center;height:100%;color:#999;">Failed to load</div>';
                                                            }}
                                                        />
                                                        {editedVersion && (
                                                            <div style={{
                                                                position: 'absolute',
                                                                top: '4px',
                                                                right: '4px',
                                                                background: editedVersion.editedImageUrl ? '#10b981' : 'var(--color-primary)',
                                                                color: 'white',
                                                                padding: '2px 6px',
                                                                borderRadius: '4px',
                                                                fontSize: '10px',
                                                                fontWeight: 600,
                                                            }}>
                                                                {editedVersion.editedImageUrl ? 'AI EDITED' : 'EDITED'}
                                                            </div>
                                                        )}
                                                    </div>

                                                    {image.alt && (
                                                        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginBottom: 'var(--space-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {image.alt}
                                                        </div>
                                                    )}

                                                    <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                                                        <button
                                                            className={`btn btn-sm ${isSelected ? 'btn-primary' : 'btn-secondary'}`}
                                                            style={{ flex: 1 }}
                                                            onClick={() => handleSelectImage(image)}
                                                        >
                                                            {isSelected ? 'Selected' : 'Select'}
                                                        </button>
                                                    </div>
                                                    <button
                                                        className="btn btn-sm btn-ghost"
                                                        style={{ flex: 1 }}
                                                        onClick={() => handleRefineClick(image)}
                                                        title="Refine image with AI (edit, enhance, modify)"
                                                    >
                                                        {editedVersion ? '✨ Refined' : '✨ Refine with AI'}
                                                    </button>

                                                    {editedVersion && (
                                                        <div style={{
                                                            marginTop: 'var(--space-2)',
                                                            padding: 'var(--space-2)',
                                                            background: editedVersion.editedImageUrl ? '#ecfdf5' : 'var(--color-success-bg, #d4edda)',
                                                            borderRadius: 'var(--radius-sm)',
                                                            fontSize: 'var(--font-size-xs)',
                                                        }}>
                                                            <div style={{ fontWeight: 600, marginBottom: 'var(--space-1)', color: editedVersion.editedImageUrl ? '#10b981' : 'var(--color-success, #155724)' }}>
                                                                {editedVersion.editedImageUrl ? '✨ AI Generated Image' : '✓ AI Edited to Match Theme'}
                                                            </div>
                                                            <div style={{ color: 'var(--color-text-secondary)' }}>
                                                                {editedVersion.description}
                                                            </div>
                                                            {editedVersion.keyChanges && editedVersion.keyChanges.length > 0 && (
                                                                <div style={{ marginTop: 'var(--space-2)' }}>
                                                                    <div style={{ fontWeight: 600, marginBottom: 'var(--space-1)' }}>Key Changes:</div>
                                                                    <ul style={{ margin: 0, paddingLeft: 'var(--space-4)', color: 'var(--color-text-secondary)' }}>
                                                                        {editedVersion.keyChanges.map((change, i) => (
                                                                            <li key={i}>{change}</li>
                                                                        ))}
                                                                    </ul>
                                                                </div>
                                                            )}
                                                            {!editedVersion.editedImageUrl && editedVersion.note && (
                                                                <div style={{ marginTop: 'var(--space-2)', fontSize: 'var(--font-size-xs)', color: '#666', fontStyle: 'italic' }}>
                                                                    {editedVersion.note}
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}


                                                </div>
                                            );
                                        })}
                                    </div>

                                    {selectedImages.length > 0 && (
                                        <div style={{ marginTop: 'var(--space-6)', padding: 'var(--space-4)', background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-md)' }}>
                                            <p style={{ marginBottom: 'var(--space-2)' }}>
                                                <strong>{selectedImages.length}</strong> image(s) selected
                                            </p>
                                            <button
                                                className="btn btn-primary"
                                                onClick={() => setShowImageSelector(false)}
                                            >
                                                Use Selected Images
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                )
            }

            {/* Prompt Assistant Modal */}
            {showPromptChat && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.75)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1100,
                    padding: 'var(--space-4)',
                }}>
                    <div className="card" style={{
                        maxWidth: '800px',
                        width: '100%',
                        height: '700px',
                        maxHeight: '90vh',
                        display: 'flex',
                        flexDirection: 'column',
                        padding: 'var(--space-4)',
                        overflow: 'hidden'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-4)' }}>
                            <h3 style={{ margin: 0 }}>AI Prompt Assistant</h3>
                            <button onClick={() => setShowPromptChat(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={24} /></button>
                        </div>
                        <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                            <PromptBuilderChat
                                userId={currentUser?.uid}
                                brandId={selectedBrand?.id}
                                originalPrompt={finalPrompt}
                                onClose={() => setShowPromptChat(false)}
                                onApplyPrompt={(p) => {
                                    setFinalPrompt(p);
                                    setShowPromptChat(false);
                                }}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Library Selector Modal */}
            {showLibrarySelector && (
                <MediaLibrarySelector
                    onSelect={handleAddFromLibrary}
                    onClose={() => setShowLibrarySelector(false)}
                    maxSelection={5 - referenceImages.length}
                />
            )}
        </div >
    );
};

export default CreativeGeneration;
